import PropTypes from "prop-types";
import { DeleteOutline, ModeEdit } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { displayDocuments } from "../../utils/helpers";
import { EmptyWrapper } from "./Elements";
import styled from "styled-components";

type TDetailsCardProps = {
  viewData: Record<string, any>;
  onDelete?: () => void;
  onEdit?: () => void;
  short?: boolean;
  horizontal?: boolean;
};

const DetailsCard = (props: TDetailsCardProps) => {
  const {
    viewData,
    onDelete,
    onEdit,
    short = false,
    horizontal = false,
  } = props;
  const [attachments, setAttachments] = useState<Record<string, any>>({});
  const [inputAttachments, setInputAttachments] = useState<Record<string, any>>(
    {},
  );

  useEffect(() => {
    const getInputAttachments = () => {
      if (!viewData || Object.keys(viewData).length === 0) return;

      const attachmentKeys = Object.keys(viewData).filter((key) =>
        key.startsWith("attachments"),
      );
      const newViewData = { ...viewData };

      let newAttachments = attachmentKeys.reduce((acc, key) => {
        acc[key] = newViewData[key];
        delete viewData[key];
        return acc;
      }, {});

      if (Object.keys(newAttachments).length <= 0) {
        return;
      }

      newAttachments = Object.keys(newAttachments).reduce((acc, key) => {
        if (key.endsWith("caption")) {
          const fileKey = attachmentKeys.find((k) => k.endsWith("file"));
          if (!fileKey) return acc;
          if (
            newAttachments[fileKey] &&
            typeof newAttachments[fileKey] === "object"
          ) {
            acc[newAttachments[key]] = newAttachments[fileKey].name;
          }
        }
        return acc;
      }, {});

      setInputAttachments(newAttachments);
    };
    const getBackendAttachments = () => {
      if (!viewData || Object.keys(viewData).length === 0) return;
      let newAttachments = Object.keys(viewData)
        .filter((key) => key.endsWith("_attachments"))
        .reduce((acc, key) => {
          acc[key] = viewData[key];
          return acc;
        }, {});

      if (Object.keys(newAttachments).length <= 0) {
        return;
      }
      newAttachments = Object.keys(newAttachments).map((key) =>
        displayDocuments(newAttachments[key]),
      );
      setAttachments(newAttachments[0]);
    };
    getBackendAttachments();
    getInputAttachments();
  }, [viewData]);

  /* one flat list of label/value pairs, so both layouts read from the same
     source and the attachments land in the same order they used to */
  const rows: [string, any][] = [
    ...Object.keys(viewData)
      .filter((key) => typeof viewData[key] !== "object" && key !== "index")
      .map(
        (key) =>
          [String(key).replaceAll("_", " "), viewData[key]] as [string, any],
      ),
    ...Object.keys(inputAttachments).map(
      (key) => [key, inputAttachments[key]] as [string, any],
    ),
    ...Object.keys(attachments).map(
      (key) =>
        [String(key).replaceAll("_", " "), attachments[key]] as [string, any],
    ),
  ];

  const cellValue = (value: any) =>
    value || value === 0 ? value : <EmptyWrapper>Empty</EmptyWrapper>;

  return (
    <DetailCard className={short ? "col-lg-6" : undefined}>
      {(onEdit || onDelete) && (
        <div className="card-actions">
          {onEdit ? (
            <button aria-label="Edit" type="button" onClick={onEdit}>
              <ModeEdit fontSize="small" />
            </button>
          ) : null}
          {onDelete ? (
            <button
              aria-label="Delete"
              type="button"
              className="danger"
              onClick={onDelete}
            >
              <DeleteOutline fontSize="small" />
            </button>
          ) : null}
        </div>
      )}

      <div className="table-scroll">
        {horizontal ? (
          /* the totals block above a register: labels on the band, figures
             underneath, so it reads as one summary strip */
          <DetailTable $inline>
            <thead>
              <tr>
                {rows.map(([label], i) => (
                  <th key={`${label}-${i}`}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {rows.map(([label, value], i) => (
                  <td key={`${label}-${i}`}>{cellValue(value)}</td>
                ))}
              </tr>
            </tbody>
          </DetailTable>
        ) : (
          <DetailTable>
            <tbody>
              {rows.map(([label, value], i) => (
                <tr key={`${label}-${i}`}>
                  <th>{label}</th>
                  <td>{cellValue(value)}</td>
                </tr>
              ))}
            </tbody>
          </DetailTable>
        )}
      </div>
    </DetailCard>
  );
};

/* the same card shell the registers and form sections use, so a details block
   sitting above a table reads as part of the same set */
const DetailCard = styled.div`
  position: relative;
  margin-bottom: 10px;
  background: var(--lp-raised);
  border: 1px solid var(--lp-border);
  border-radius: var(--lp-r-md);
  box-shadow: var(--lp-shadow-sm);
  overflow: hidden;

  .table-scroll {
    overflow-x: auto;
  }

  .card-actions {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 2;
    display: flex;
    gap: 2px;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      padding: 0;
      border: none;
      border-radius: var(--lp-r-pill);
      background: var(--lp-raised);
      color: var(--lp-muted);
      transition: all 0.15s ease;

      &:hover {
        background: var(--lp-primary-soft);
        color: var(--lp-primary);
      }

      &.danger:hover {
        background: #fdecec;
        color: #d70040;
      }
    }
  }
`;

const DetailTable = styled.table<{ $inline?: boolean }>`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin: 0;
  font-variant-numeric: tabular-nums;

  ${({ $inline }) =>
    $inline
      ? `
    thead th {
      text-align: left;
      white-space: nowrap;
      text-transform: uppercase;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.08em;
      color: var(--lp-muted);
      background: var(--lp-tile);
      padding: 11px 16px;
      border-bottom: 1px solid var(--lp-border);
    }

    tbody td {
      white-space: nowrap;
      font-size: 15px;
      font-weight: 700;
      color: var(--lp-text);
      background: var(--lp-raised);
      padding: 14px 16px;
    }
  `
      : `
    tbody tr:hover td {
      background: var(--lp-primary-soft);
    }

    tbody tr:last-child th,
    tbody tr:last-child td {
      border-bottom: none;
    }

    /* the label column carries the tint, matching the table heads */
    th {
      width: 32%;
      max-width: 320px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: left;
      vertical-align: top;
      text-transform: capitalize;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.03em;
      color: var(--lp-muted);
      background: var(--lp-tile);
      padding: 10px 16px;
      border-bottom: 1px solid var(--lp-border);
      border-right: 1px solid var(--lp-border);
    }

    td {
      font-size: 13px;
      font-weight: 500;
      color: var(--lp-text);
      background: var(--lp-raised);
      padding: 10px 16px;
      vertical-align: top;
      word-break: break-word;
      border-bottom: 1px solid var(--lp-border);
      transition: background-color 0.15s ease;
    }

    @media (max-width: 960px) {
      th {
        max-width: 140px;
      }
    }
  `}
`;

DetailsCard.propTypes = {
  viewData: PropTypes.shape({}),
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
};

export default DetailsCard;
